import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import PageMeta from "../../components/seo/PageMeta";
import { decodeShare } from "../../utils/shareLink";
import {
  HeartHandshake, Stethoscope, Microscope, FileText, ShieldCheck, Clock,
  AlertCircle, Printer, ArrowRight, CheckCircle2, Sparkles, Link2Off,
} from "lucide-react";

const DISCIPLINE_META = {
  nurse:     { Icon: HeartHandshake, label: "Nurse Skills Checklist",      accent: "bg-trital-medical-blue-soft text-trital-navy" },
  therapist: { Icon: Stethoscope,     label: "Therapist Skills Checklist",  accent: "bg-red-50 text-trital-red" },
  other:     { Icon: Microscope,      label: "Allied Health Checklist",     accent: "bg-trital-medical-blue-soft text-trital-navy" },
};

function fmtDateTime(iso) {
  if (!iso) return null;
  try {
    const d = typeof iso === "number" ? new Date(iso) : new Date(iso);
    return d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

/* ------------------------------------------------------------------ */

export default function SharedChecklist() {
  const [result, setResult] = useState(null);

  useEffect(() => {
    const decode = () => setResult(decodeShare(window.location.hash));
    decode();
    window.addEventListener("hashchange", decode);
    return () => window.removeEventListener("hashchange", decode);
  }, []);

  const handlePrint = () => window.print();

  if (!result) {
    return (
      <div className="min-h-screen bg-white">
        <PublicNav />
        <div className="max-w-3xl mx-auto px-5 lg:px-8 py-24 text-center text-slate-500">Loading…</div>
        <PublicFooter />
      </div>
    );
  }

  if (!result.ok) return <ErrorState reason={result.reason} meta={result.meta} />;

  return <ReadOnlyView payload={result.payload} meta={result.meta} onPrint={handlePrint} />;
}

/* ------------------------------------------------------------------ */

function ReadOnlyView({ payload, meta, onPrint }) {
  const checklists = payload?.checklists || [];
  const totals = payload?.clinicianSummary?.totalsAcrossAllChecklists || {};
  const submitted = checklists.filter((c) => c.status === "submitted");

  return (
    <div className="min-h-screen bg-white" data-testid="shared-checklist">
      <PageMeta
        title="Shared Skills Checklist | TRITAL Care®"
        description="A clinician-shared skills checklist via TRITAL Care®."
        canonical="https://www.tritalcare.com/c"
      />
      <PublicNav />

      <section className="relative overflow-hidden bg-trital-mesh" data-testid="shared-hero">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-14 lg:pt-20 lg:pb-16">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
                <FileText className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} />
                <span className="overline text-trital-navy">Shared by a clinician</span>
              </div>
              <h1 className="mt-6 font-display font-black text-4xl lg:text-[52px] leading-[1.04] tracking-tight text-slate-900 text-balance">
                A TRITAL Care® clinician shared their <span className="text-trital-navy">competency record</span> with you.
              </h1>
              <p className="mt-5 text-slate-600 max-w-xl leading-relaxed">
                This is a read-only view. The data is encoded in the link itself — nothing is stored on our servers. Print
                or save this page as a PDF to keep a permanent copy.
              </p>
              <div className="mt-6 flex flex-wrap gap-2 text-[12px] text-slate-600">
                {meta?.expiresAt && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200">
                    <Clock className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} />
                    Link expires {fmtDateTime(meta.expiresAt)}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200">
                  <ShieldCheck className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} />
                  No data stored on TRITAL servers
                </span>
              </div>

              <div className="mt-7 flex flex-wrap gap-2">
                <button
                  onClick={onPrint}
                  className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-trital-navy text-white text-[13.5px] font-semibold btn-lift hover:bg-trital-navy-deep"
                  data-testid="shared-btn-print"
                >
                  <Printer className="w-3.5 h-3.5" strokeWidth={1.8} /> Print / Save as PDF
                </button>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full bg-trital-red text-white text-[13.5px] font-semibold btn-lift hover:bg-trital-red-deep"
                  data-testid="shared-btn-contact"
                >
                  Talk to TRITAL Care® <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-navy p-6 lg:p-7 relative overflow-hidden" data-testid="shared-totals">
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-trital-medical-blue-soft blur-3xl" />
                <div className="relative">
                  <div className="overline text-trital-red flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" strokeWidth={1.8} /> Summary
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <Stat label="Specialties attested" value={totals.attested ?? 0} tone="navy" />
                    <Stat label="Competencies rated"   value={totals.rated ?? 0} tone="navy" />
                    <Stat label="Disciplines submitted" value={submitted.length} tone="green" />
                    <Stat label="Drafts included"       value={Math.max(0, (totals.drafts ?? 0) - submitted.length)} tone="amber" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16" data-testid="shared-cards">
        <div className="grid md:grid-cols-3 gap-5">
          {checklists.map((c, i) => (
            <ChecklistCard key={c.id || i} c={c} />
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24">
        <div className="rounded-3xl bg-slate-50 border border-slate-200 p-8 lg:p-10 text-center">
          <div className="w-12 h-12 mx-auto rounded-2xl bg-white border border-slate-200 flex items-center justify-center">
            <FileText className="w-5 h-5 text-trital-navy" strokeWidth={1.8} />
          </div>
          <h3 className="mt-4 font-display font-bold text-2xl text-slate-900">Want to verify and follow up?</h3>
          <p className="mt-2 text-[14.5px] text-slate-600 max-w-lg mx-auto">
            TRITAL Care® recruiters can primary-source verify any specialty on this record and coordinate credentialing for your facility.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Link
              to="/contact"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-trital-navy text-white text-[13px] font-semibold btn-lift hover:bg-trital-navy-deep"
              data-testid="shared-cta-contact"
            >
              Talk to a recruiter <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
            </Link>
            <Link
              to="/compliance"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-300 text-[13px] font-semibold text-trital-navy hover:bg-slate-100"
              data-testid="shared-cta-compliance"
            >
              How recruiter review works
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneClass =
    tone === "green" ? "text-emerald-600" : tone === "amber" ? "text-amber-500" : "text-trital-navy";
  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className={`mt-1 font-display font-black text-3xl ${toneClass}`}>{value}</div>
    </div>
  );
}

function ChecklistCard({ c }) {
  const meta = DISCIPLINE_META[c.id] || { Icon: FileText, label: c.label || "Checklist", accent: "bg-slate-100 text-slate-600" };
  const { Icon } = meta;
  const entries = c.entries || {};
  const started = Object.entries(entries).filter(([, e]) =>
    e && (e.yearsOfExperience !== "" || e.overallConfidence > 0 || e.notes?.length || Object.keys(e.items || {}).length > 0)
  );

  return (
    <motion.div
      initial={{ opacity: 1, y: 0 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      className="rounded-3xl bg-white border border-slate-200 p-6 shadow-card relative overflow-hidden"
      data-testid={`shared-card-${c.id}`}
    >
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-trital-medical-blue-soft blur-2xl opacity-40" />
      <div className="relative">
        <div className="flex items-center justify-between">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${meta.accent}`}>
            <Icon className="w-6 h-6" strokeWidth={1.8} />
          </div>
          <StatusPill status={c.status} />
        </div>
        <h3 className="mt-5 font-display font-bold text-[19px] text-slate-900">{c.label || meta.label}</h3>
        {c.submittedAt && (
          <div className="mt-1 text-[11.5px] text-slate-500 inline-flex items-center gap-1">
            <Clock className="w-3 h-3" strokeWidth={1.8} /> Submitted {fmtDateTime(c.submittedAt)}
          </div>
        )}

        {started.length === 0 ? (
          <div className="mt-5 text-[13px] text-slate-500 italic">No specialties started on this checklist.</div>
        ) : (
          <ul className="mt-5 space-y-3 text-[13px]">
            {started.map(([sid, e]) => {
              const ratedCount = Object.keys(e.items || {}).length;
              return (
                <li key={sid} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-display font-bold text-slate-900 truncate">{sid}</span>
                    {e.attested && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wider">
                        <CheckCircle2 className="w-2.5 h-2.5" strokeWidth={2} /> Attested
                      </span>
                    )}
                  </div>
                  <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-slate-600">
                    {e.yearsOfExperience !== "" && (
                      <span><span className="text-slate-400">Years: </span><span className="font-semibold text-slate-800">{e.yearsOfExperience}</span></span>
                    )}
                    {e.overallConfidence > 0 && (
                      <span><span className="text-slate-400">Confidence: </span><span className="font-semibold text-slate-800">{e.overallConfidence}/4</span></span>
                    )}
                    <span><span className="text-slate-400">Rated: </span><span className="font-semibold text-slate-800">{ratedCount}</span></span>
                  </dl>
                  {e.notes && (
                    <p className="mt-2 text-[12px] text-slate-600 italic leading-relaxed">"{e.notes}"</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </motion.div>
  );
}

function StatusPill({ status }) {
  if (status === "submitted")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10.5px] font-bold uppercase tracking-wider">
        <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> Submitted
      </span>
    );
  if (status === "draft")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700 text-[10.5px] font-bold uppercase tracking-wider">
        <Clock className="w-3 h-3" strokeWidth={2} /> Draft
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-500 text-[10.5px] font-bold uppercase tracking-wider">
      Not started
    </span>
  );
}

/* ------------------------------------------------------------------ */

function ErrorState({ reason, meta }) {
  const isExpired = reason === "expired";
  const title = useMemo(() => {
    if (isExpired) return "This link has expired.";
    if (reason === "missing") return "No checklist data in this link.";
    if (reason === "corrupted") return "This link appears to be corrupted.";
    if (reason === "version") return "This link was created with an older version.";
    return "We couldn't read this link.";
  }, [reason, isExpired]);

  return (
    <div className="min-h-screen bg-white" data-testid="shared-error">
      <PageMeta title="Shared Checklist | TRITAL Care®" description="A shared clinician checklist could not be opened." />
      <PublicNav />
      <section className="max-w-3xl mx-auto px-5 lg:px-8 pt-24 pb-32">
        <div className="rounded-3xl bg-white border border-slate-200 p-8 lg:p-10 shadow-card text-center">
          <div className={`w-14 h-14 mx-auto rounded-2xl flex items-center justify-center ${isExpired ? "bg-amber-50 text-amber-500" : "bg-red-50 text-trital-red"}`}>
            {isExpired ? <Link2Off className="w-7 h-7" strokeWidth={1.8} /> : <AlertCircle className="w-7 h-7" strokeWidth={1.8} />}
          </div>
          <h1 className="mt-5 font-display font-black text-3xl lg:text-4xl text-slate-900 text-balance">{title}</h1>
          <p className="mt-3 text-slate-600 max-w-md mx-auto leading-relaxed">
            {isExpired && meta?.expiresAt
              ? `The clinician's link expired ${fmtDateTime(meta.expiresAt)}. Ask them to regenerate a fresh shareable link from their "My Checklists" page.`
              : "Ask the clinician to regenerate a shareable link from their 'My Checklists' page and send the new URL."}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Link
              to="/c/scan"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-trital-navy text-white text-[13px] font-semibold btn-lift hover:bg-trital-navy-deep"
              data-testid="shared-error-scan"
            >
              Scan a new code
            </Link>
            <Link
              to="/my-checklists"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-300 text-[13px] font-semibold text-trital-navy hover:bg-slate-50"
              data-testid="shared-error-my-checklists"
            >
              Open My Checklists
            </Link>
            <Link
              to="/compliance/checklists"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-300 text-[13px] font-semibold text-trital-navy hover:bg-slate-50"
              data-testid="shared-error-hub"
            >
              Checklist hub
            </Link>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
